import type { NetPlayerRow } from '../../networking/transport';
import { useEffect, useState } from 'react';
import { CIVS } from '../../gameplay/civilisations';
import { RES_NAMES } from '../../gameplay/types';
import { savePlayerGame } from '../../persistence/saves';
import { useApp, type CommandButton } from '../store';
import { ActionIcon, ResIcon, portrait } from '../icons';
import { SettingsPanel } from '../menus/SettingsScreen';
import { Minimap } from './Minimap';
import { Results } from './Results';
import { audio } from '../../engine/audio/audio';

const RES_CLASS = ['food', 'wood', 'gold', 'stone'];

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const h = Math.floor(m / 60);
  return `${h ? h + ':' : ''}${String(m % 60).padStart(h ? 2 : 1, '0')}:${String(s).padStart(2, '0')}`;
}

function Cost({ cost }: { cost: number[] }) {
  return (
    <div className="cost">
      {cost.map((c, i) => c > 0 && <span key={i} className={`res ${RES_CLASS[i]}`} style={{ minWidth: 0 }} title={RES_NAMES[i]}><ResIcon r={i} />{c}</span>)}
    </div>
  );
}

/**
 * Keep the tooltip clear of the command card: to its left when there is room, otherwise above it,
 * so it never covers the buttons being browsed.
 */
function tipStyle(card: DOMRect, y: number): React.CSSProperties {
  if (card.left > 320) return { right: window.innerWidth - card.left + 12, bottom: Math.max(10, window.innerHeight - y - 40) };
  return { left: Math.max(10, card.left), bottom: window.innerHeight - card.top + 10 };
}

export function Hud() {
  const hud = useApp((s) => s.hud);
  const client = useApp((s) => s.client);
  const notes = useApp((s) => s.notifications);
  const menuOpen = useApp((s) => s.menuOpen);
  const prefs = useApp((s) => s.prefs);
  const [tip, setTip] = useState<{ b: CommandButton; y: number; card: DOMRect } | null>(null);
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!hud || !client) return null;
  const local = client.local >= 0 ? client.local : 0;
  const civKey = CIVS[client.sim.world.players[local].civ].key;
  const color = client.sim.world.players[local].color;
  const portraitOf = (key: string, owner: number) => {
    const p = client.sim.world.players[owner];
    return portrait(key, p ? CIVS[p.civ].key : civKey, p ? p.color : 0xffffff);
  };
  const now = performance.now();
  const groups: Record<string, CommandButton[]> = {};
  for (const b of hud.buttons) (groups[b.group] ??= []).push(b);
  const prim = hud.primary;
  return (
    <div className="hud" style={{ ['--ui-scale' as string]: prefs.uiScale }}>
      <div className={`topbar${hud.netPlayers?.length ? ' online' : ''}`}>
        <div className="topbar-title" aria-hidden="true">Four Crowns</div>
        {hud.res.map((r, i) => (
          <div key={i} className={`res ${RES_CLASS[i]}`} title={`${RES_NAMES[i]}: ${r} stored, ${hud.workers[i]} gathering`}><ResIcon r={i} />{r}<span className="workers">/{hud.workers[i]}</span></div>
        ))}
        <div className={`res pop ${hud.pop >= hud.popCap ? 'capped' : ''}`} title="Population"><ResIcon r="pop" />{hud.pop}/{hud.popCap}</div>
        <div className="age">{hud.ageName}</div>
        <div className="muted small">{hud.civName}</div>
        <div className="spacer" />
        {hud.wonderTimers.map((wt) => <div key={wt.team} className="small" style={{ color: 'var(--warn)' }}>Wonder: {fmtTime(wt.seconds)}</div>)}
        {hud.economic && !hud.gameOver && (
          <div className="econ-lead small" title={`Economic victory: the first to gather ${hud.economic.target.toLocaleString('en')} resources wins. Shown: the player closest to it.`}>
            <i style={{ background: hud.economic.color }} />Economic: {hud.economic.name} {Math.floor((hud.economic.gathered / hud.economic.target) * 100)}%
            <span className="econ-bar"><span style={{ width: `${Math.min(100, (hud.economic.gathered / hud.economic.target) * 100)}%`, background: hud.economic.color }} /></span>
          </div>
        )}
        {hud.network && <NetworkStatus line={hud.network} rows={hud.netPlayers} />}
        {!audio.running && (
          <button className="icon-btn sound-off" onClick={() => audio.test()} title="The browser has not started sound yet. Click to turn it on.">Sound off: click to enable</button>
        )}
        <div className="clock" title="Game time">{fmtTime(hud.time)}{hud.speed !== hud.baseSpeed ? ` (${hud.speed}×)` : ''}{hud.paused ? ' (paused)' : ''}</div>
        {!client.transport.networked && client.local >= 0 && !hud.gameOver && !hud.replay && (
          <button className="icon-btn" onClick={() => (client.paused = !client.paused)} title="Pause or resume the game (F3)">{hud.paused ? 'Resume' : 'Pause'}</button>
        )}
        <div className="clock real" title="Time of day">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        <button className="icon-btn" onClick={() => useApp.getState().setMenuOpen(true)}>Menu</button>
      </div>

      <div className="notifications">
        {notes.filter((n) => now - n.time < 8000).map((n) => (
          <div key={n.id} className={`note ${n.kind}`} onClick={() => n.x !== undefined && client.cam.jumpTo(n.x, n.z!)}>{n.text}</div>
        ))}
      </div>

      {hud.mode !== 'normal' && (
        <div className="mode-hint">
          {hud.wall ? (
            <>{hud.wall.name}: {hud.wall.started ? 'click to finish (Shift-click to keep going)' : 'click to set the start'} · Right-click to cancel{hud.wall.started && <> · {hud.wall.pieces} sections <Cost cost={hud.wall.cost} /></>}</>
          ) : hud.mode === 'place' ? 'Left-click to place (Shift to place several) · Right-click to cancel' : hud.mode === 'attackMove' ? 'Attack-move: choose a destination' : hud.mode === 'patrol' ? 'Patrol: choose a point' : hud.mode === 'guard' ? 'Guard: choose a friendly unit' : 'Choose a rally point'}
        </div>
      )}
      {hud.desync && <div className="banner">{hud.desync}</div>}
      {hud.paused && !menuOpen && !hud.gameOver && !hud.replay && <div className="paused-banner">Paused<span className="small muted">F3 or Resume to carry on</span></div>}

      {prefs.showFps ? <PerfOverlay /> : (
        <div className="scoreboard">
          {hud.players.map((p) => (
            <div key={p.id} className="pl" style={{ opacity: p.defeated ? 0.4 : 1 }}><i style={{ background: p.color }} />{p.name} <span className="muted">{p.civ} · {['I', 'II', 'III', 'IV', 'V'][p.age]}</span> {p.score}</div>
          ))}
        </div>
      )}

      {hud.idleVillagers > 0 && client.local >= 0 && (
        <button className="icon-btn idle-btn" onClick={() => client.selectIdleVillager()} title="Select idle villager or fishing boat (.)">Idle workers: {hud.idleVillagers}</button>
      )}

      {hud.replay && (
        <div className="panel replay-bar">
          <span className="small">Replay {fmtTime(hud.replay.tick / 20)} / {fmtTime(hud.replay.total / 20)}</span>
          <button className="btn" onClick={() => (client.paused = !client.paused)}>{client.paused ? 'Play' : 'Pause'}</button>
          <div className="speeds">
            {[0.5, 1, 2, 4, 8].map((s) => <button key={s} className={`btn ${hud.speed === s ? 'primary' : ''}`} onClick={() => (client.speed = s)}>{s}×</button>)}
          </div>
          <button className="btn" onClick={() => client.fastForward(1200)} title="Skip ahead one minute">+1 min</button>
          <select onChange={(e) => { client.renderer.viewer = Number(e.target.value); client.renderer.revealAll = Number(e.target.value) < 0; client.renderer.updateFog(true); }} defaultValue={-1} aria-label="Perspective">
            <option value={-1}>All players</option>
            {hud.players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      )}

      <div className="bottom-bar">
        <Minimap client={client} />
        <div className="panel sel-panel">
          {prim ? (
            <>
              <div className="sel-left">
                <img className="portrait" src={portraitOf(prim.kind, prim.owner)} alt="" />
                {groups.age?.map((b) => (
                  <div key={b.id} className="age-up">
                    <button className={`portrait age-btn ${b.disabled ? 'disabled' : ''}`} title={`${b.tooltip}${b.disabled ? `\n${b.disabled}` : ''}`} aria-label={b.label}
                      onClick={() => { if (!b.disabled) client.button(b.id); else client.audio?.ui('error'); }}>
                      <ActionIcon name={b.icon} />
                    </button>
                    <div className="small">
                      <div className="age-name">{b.label}</div>
                      {b.cost && <Cost cost={b.cost} />}
                      {b.disabled && <div className={/progress|advancing/i.test(b.disabled) ? 'muted' : 'why'}>{b.disabled}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="sel-info">
                <div className="name">{prim.name}{hud.selection.length === 1 && hud.selection[0].count > 1 ? ` ×${hud.selection[0].count}` : ''}</div>
                <div className="muted small">{prim.ownerName}{prim.built < 1 ? ` · Under construction ${Math.round(prim.built * 100)}%` : ''}</div>
                {prim.category !== 'resource' && (<><div className="hpbar"><div style={{ width: `${(prim.hp / prim.maxHp) * 100}%` }} /></div><div className="small">{prim.hp} / {prim.maxHp}</div></>)}
                {hud.selection.length > 1 || (hud.selection[0]?.count ?? 0) > 1 ? (
                  <div className="multi-sel" style={{ marginTop: 6 }}>
                    {hud.selection.map((g) => (
                      <div key={g.kind} className="chip" title={g.name} onClick={(e) => client.setSelection(e.shiftKey ? [g.id] : g.ids)}>
                        <img src={portraitOf(g.kind, g.owner)} alt="" />
                        <span>{g.count}</span>
                        <div className="mini-hp" style={{ width: `${(g.hp / g.maxHp) * 100}%` }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="stats" style={{ marginTop: 6 }}>
                    {prim.attack && <div>Attack <b>{prim.attack}</b></div>}
                    {prim.armor && <div>Armour <b>{prim.armor}</b></div>}
                    {prim.range && <div>Range <b>{prim.range}</b></div>}
                    {prim.los > 0 && <div>Sight <b>{prim.los}</b></div>}
                    {prim.speed && <div>Speed <b>{prim.speed}</b></div>}
                    {prim.carry && <div>Carrying <b>{prim.carry}</b></div>}
                    {prim.garrison && <div>Inside <b>{prim.garrison}</b></div>}
                    {prim.resourceLeft && <div>Remaining <b>{prim.resourceLeft}</b></div>}
                  </div>
                )}
                {prim.queue.length > 0 && (
                  <div className="queue">
                    {prim.queue.map((q, i) => (
                      <div key={i} className="q" title={`${q.label} (click to cancel)`} onClick={() => client.button(`cancel:${i}`)}>
                        {q.isTech ? <ActionIcon name={q.key.startsWith('age') ? `age${Number(q.key.slice(3)) - 1}` : 'tech'} /> : <img src={portraitOf(q.key, local)} alt="" />}
                        <div className="prog" style={{ width: `${q.progress * 100}%` }} />
                      </div>
                    ))}
                  </div>
                )}
                {prim.repair && hud.selection.length <= 1 && (
                  <div className="repair-row">
                    <button className="btn" onClick={() => client.repairSelected()} title="Send up to three villagers to repair. A full repair costs half the building's price, paid as the work goes on.">
                      <ActionIcon name="repair" />Repair
                    </button>
                    <div className="small">
                      <div className="muted">Full repair needs</div>
                      <Cost cost={prim.repair.cost} />
                    </div>
                    {prim.repair.repairers > 0 && <div className="small muted">{prim.repair.repairers} repairing</div>}
                  </div>
                )}
                {hud.selection.length <= 1 && prim.description && <div className="muted small" style={{ marginTop: 6 }}>{prim.description}</div>}
              </div>
            </>
          ) : (
            <div className="muted small" style={{ alignSelf: 'center' }}>Select units or buildings. Right-click to command. Press H for your Town Centre.</div>
          )}
        </div>
        {hud.buttons.some((b) => b.group !== 'age') && (
          <div className="panel cmd-card" onMouseLeave={() => setTip(null)}>
            {(['train', 'research', 'build', 'action', 'formation', 'stance'] as const).map((g) => groups[g] && (
              <FragmentGroup key={g} label={g === 'train' ? 'Train' : g === 'research' ? 'Research' : g === 'build' ? 'Build' : g === 'formation' ? 'Formation' : g === 'stance' ? 'Stance' : ''}>
                {groups[g].map((b) => {
                  const isPortrait = b.group === 'train' || b.group === 'build';
                  const active = b.disabled === 'Active';
                  return (
                    <button key={b.id} className={`cmd-btn ${b.disabled && !active ? 'disabled' : ''} ${active ? 'active' : ''}`}
                      onClick={() => { if (!b.disabled || active) client.button(b.id); else client.audio?.ui('error'); }}
                      onMouseEnter={(e) => setTip({ b, y: e.clientY, card: e.currentTarget.closest('.cmd-card')!.getBoundingClientRect() })}
                      aria-label={b.label}>
                      {isPortrait ? <img src={portraitOf(b.icon, local)} alt="" /> : <ActionIcon name={b.icon} />}
                      {b.hotkey && <span className="hk">{b.hotkey}</span>}
                      <span className="lbl">{b.label}</span>
                    </button>
                  );
                })}
              </FragmentGroup>
            ))}
          </div>
        )}
      </div>

      {tip && (
        <div className="tooltip" style={tipStyle(tip.card, tip.y)}>
          <div style={{ fontWeight: 700, color: 'var(--gold-bright)' }}>{tip.b.label}</div>
          <div>{tip.b.tooltip}</div>
          {tip.b.cost && <Cost cost={tip.b.cost} />}
          {tip.b.disabled && tip.b.disabled !== 'Active' && <div className="why">{tip.b.disabled}</div>}
        </div>
      )}

      {menuOpen && <GameMenu />}
      <ChatBox />
      {hud.gameOver && <GameOver won={hud.gameOver.won} />}
      {void color}
    </div>
  );
}

const NET_STATUS: Record<NetPlayerRow['status'], { label: string; color: string }> = {
  good: { label: 'Good', color: '#5fd068' },
  slow: { label: 'Slow', color: '#e8b830' },
  lagging: { label: 'Lagging', color: '#e8503a' },
  left: { label: 'Left (computer playing)', color: '#8a8a8a' },
};

/**
 * The multiplayer status line (unchanged), which opens a panel with one row per player: ping,
 * frame rate, how far behind their game is, how long the game waited for them in the last 30 s,
 * and a good, slow or lagging indicator.
 */
function NetworkStatus({ line, rows }: { line: string; rows: NetPlayerRow[] | null }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return <div className="small muted">{line}</div>;
  const worst = rows.some((r) => r.status === 'lagging') ? 'lagging' : rows.some((r) => r.status === 'slow') ? 'slow' : 'good';
  return (
    <div className="net-status">
      <button className="net-line small" onClick={() => setOpen(!open)} title="Connection to each player" aria-expanded={open}>
        <i className="net-dot" style={{ background: NET_STATUS[worst].color }} />{line}<span className="net-caret">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="net-panel panel" onMouseDown={(e) => e.stopPropagation()}>
          {rows.map((r) => (
            <div key={r.player} className="net-row">
              <i className="net-dot" style={{ background: NET_STATUS[r.status].color }} title={NET_STATUS[r.status].label} />
              <span className="net-swatch" style={{ background: r.color }} />
              <span className="net-name">{r.you ? `${r.name} (you)` : r.name}</span>
              <span className="net-state" style={{ color: NET_STATUS[r.status].color }}>{NET_STATUS[r.status].label}</span>
              <span className="net-figs muted">
                {r.you ? '' : r.pingMs !== null ? `ping ${r.pingMs} ms · ` : 'via host · '}
                {r.fps !== null ? `${r.fps} fps` : 'fps n/a'}
                {r.behindSec !== null && r.behindSec >= 0.5 ? ` · ${r.behindSec.toFixed(1)} s behind` : ''}
                {!r.you ? ` · held up ${(r.heldUpMs / 1000).toFixed(1)} s` : ''}
              </span>
            </div>
          ))}
          <div className="small muted net-help">Held up: how long the game waited for that player in the last 30 seconds. A low frame rate means their computer is struggling; a high ping means their connection is.</div>
        </div>
      )}
    </div>
  );
}

function ChatBox() {
  const open = useApp((s) => s.chatOpen);
  const client = useApp((s) => s.client);
  const [text, setText] = useState('');
  const [allies, setAllies] = useState(false);
  if (!open || !client) return null;
  const close = () => useApp.getState().setChatOpen(false);
  return (
    <div className="panel" style={{ position: 'absolute', left: '50%', bottom: 200, transform: 'translateX(-50%)', padding: 8, display: 'flex', gap: 8, zIndex: 20 }}>
      <input autoFocus type="text" value={text} placeholder="Message (Enter to send, Esc to close)" style={{ width: 320 }}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            if (text.trim()) client.chat(text.trim(), allies);
            setText('');
            close();
          } else if (e.key === 'Escape') close();
        }} />
      <label className="small"><input type="checkbox" checked={allies} onChange={(e) => setAllies(e.target.checked)} /> Allies only</label>
    </div>
  );
}

function FragmentGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      {label && <div className="cmd-sep">{label}</div>}
      {children}
    </>
  );
}

function PerfOverlay() {
  const client = useApp((s) => s.client);
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 500);
    return () => clearInterval(t);
  }, []);
  if (!client) return null;
  const ft = client.frameTimes;
  const avg = ft.reduce((a, b) => a + b, 0) / Math.max(1, ft.length);
  const sorted = [...ft].sort((a, b) => b - a);
  const low = sorted[Math.floor(sorted.length * 0.01)] ?? avg;
  const st = client.renderer.stats;
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  return (
    <div className="perf">
      {`FPS ${(1000 / avg).toFixed(0)}  1% low ${(1000 / low).toFixed(0)}
frame ${avg.toFixed(1)} ms  render ${st.renderMs.toFixed(1)} ms
sim tick ${client.simMs.toFixed(2)} ms  tick ${client.sim.tick}
draw calls ${st.drawCalls}  tris ${(st.triangles / 1000).toFixed(0)}k  passes ${st.passes}  res ${Math.round(client.renderer.dynamicScale * 100)}%
${st.effects}
entities ${client.sim.world.entityCount}  geo ${st.geometries} tex ${st.textures}${mem ? `\nheap ${(mem.usedJSHeapSize / 1048576).toFixed(0)} MB` : ''}`}
    </div>
  );
}

function GameMenu() {
  const client = useApp((s) => s.client)!;
  const [view, setView] = useState<'main' | 'settings'>('main');
  const [msg, setMsg] = useState('');
  const close = () => useApp.getState().setMenuOpen(false);
  const multiplayer = client.transport.networked && client.local >= 0;
  useEffect(() => {
    if (multiplayer) return;
    const wasPaused = client.paused;
    client.paused = true;
    return () => {
      client.paused = wasPaused;
    };
  }, [client, multiplayer]);
  return (
    <div className="overlay" onMouseDown={(e) => e.stopPropagation()}>
      <div className="menu-card" style={view === 'settings' ? { width: 'min(900px, 100%)' } : undefined}>
        {view === 'settings' ? (
          <>
            <SettingsPanel />
            <button className="btn" style={{ marginTop: 12 }} onClick={() => setView('main')}>Back</button>
          </>
        ) : (
          <>
            <h2>Game Menu</h2>
            <button className="menu-btn" onClick={close}>Resume</button>
            {multiplayer && (
              <button className="menu-btn" onClick={() => { client.issue({ t: 'saveGame', name: `Save ${new Date().toLocaleString()}` }); setMsg('Save requested; all players will save at the same moment.'); }}>Save Game (all players)</button>
            )}
            {!multiplayer && client.local >= 0 && (
              <button className="menu-btn" onClick={async () => {
                try {
                  const name = await savePlayerGame(client.sim);
                  setMsg(`Saved as "${name}"`);
                } catch (e) {
                  setMsg(`Save failed: ${e instanceof Error ? e.message : e}`);
                }
              }}>Save Game</button>
            )}
            <button className="menu-btn" onClick={() => setView('settings')}>Settings</button>
            {client.local >= 0 && <button className="menu-btn" onClick={() => { client.issue({ t: 'resign' }); close(); }}>Resign</button>}
            <button className="menu-btn" onClick={() => { close(); useApp.getState().setScreen('menu'); }}>Exit to Main Menu</button>
            {msg && <p className="small muted">{msg}</p>}
          </>
        )}
      </div>
    </div>
  );
}

function GameOver({ won }: { won: boolean }) {
  const hud = useApp((s) => s.hud)!;
  const client = useApp((s) => s.client)!;
  // The fog of war is lifted at the end; the results can be put away to look around the map.
  const [viewing, setViewing] = useState(false);
  const [results, setResults] = useState(false);
  const go = hud.gameOver!;
  const title = client.local < 0 ? 'Game Over' : won ? 'Victory' : 'Defeat';
  const who = go.winners.length === 0 ? 'Nobody wins' : go.winners.length === 1 && go.winners[0] === 'You' ? 'You win' : `${go.winners.join(' and ')} win${go.winners.length === 1 ? 's' : ''}`;
  const label = go.reason === 'Time limit (score)' ? 'Time limit victory' : go.reason === 'Last human out' ? 'no human players left' : `${go.reason} victory`;
  const summary = go.reason ? `${who}: ${label}` : who;
  if (results) {
    return <Results history={client.history} players={hud.players} onClose={() => setResults(false)} />;
  }
  if (viewing) {
    return (
      <div className="result-bar">
        <b>{title}</b>
        <span className="muted small">{summary}. The whole map is revealed. Scroll around freely.</span>
        <button className="btn" onClick={() => setViewing(false)}>Summary</button>
        <button className="btn" onClick={() => setResults(true)}>Results</button>
        <button className="btn" onClick={() => useApp.getState().setScreen('menu')}>Main Menu</button>
      </div>
    );
  }
  return (
    <div className="overlay">
      <div className="menu-card">
        <h1 className="game-title" style={{ fontSize: 44 }}>{title}</h1>
        <p className="victory-line">{summary}</p>
        {go.detail && <p className="muted small">{go.winners.length === 1 && go.winners[0] === 'You' ? 'You' : go.winners.join(' and ')} {go.detail}.</p>}
        <div className="final-table">
          <div className="row spread small muted"><span>Player</span><span>Gathered · Score</span></div>
          {hud.players.map((p) => (
            <div key={p.id} className={`row spread small ${p.winner ? 'winner' : ''}`}>
              <span><span style={{ color: p.color }}>■</span> {p.name} ({p.civ}) <b className="tag">{p.winner ? 'Winner' : p.defeated ? 'Defeated' : 'Survived'}</b></span>
              <span>{p.gathered.toLocaleString('en')} · {p.score.toLocaleString('en')}</span>
            </div>
          ))}
        </div>
        <p className="muted small">A replay of this match was saved and can be watched from Replays.</p>
        <button className="menu-btn" onClick={() => setResults(true)}>Results</button>
        <button className="menu-btn" onClick={() => setViewing(true)}>View the Map</button>
        <button className="menu-btn" onClick={() => useApp.getState().setScreen('menu')}>Main Menu</button>
      </div>
    </div>
  );
}
